import { Box } from '@mui/material';

interface PullTabProps {
  orientation: 'vertical' | 'horizontal';
  onClick?: () => void;
  position?: 'left' | 'right' | 'top' | 'bottom';
  sizeScale?: number; // 1 = 100% size, 0.75 = 75% size
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  cursor?: 'pointer' | 'col-resize' | 'row-resize';
}

/**
 * PullTab component matching LEGO Spike Prime design
 * Features two parallel lines with a rounded nub appearance
 */
const PullTab = ({
  orientation,
  onClick,
  position = 'left',
  sizeScale = 1,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
  cursor = 'pointer',
}: PullTabProps) => {
  const isVertical = orientation === 'vertical';
  const verticalWidth = 24 * sizeScale;
  const verticalHeight = 56 * sizeScale;
  const horizontalWidth = 56 * sizeScale;
  const horizontalHeight = 24 * sizeScale;
  const lineThickness = 2 * sizeScale;
  const lineLength = 16 * sizeScale;
  const lineGap = 4 * sizeScale;
  const radius = 8 * sizeScale;

  return (
    <Box
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={(theme) => ({
        cursor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: isVertical ? verticalWidth : horizontalWidth,
        height: isVertical ? verticalHeight : horizontalHeight,
        // Opaque background so editor text doesn't show through
        bgcolor: theme.vars?.palette?.background?.paper,
        border: 1,
        borderColor: theme.vars?.palette?.divider,
        borderRadius:
          position === 'left'
            ? `${radius}px 0 0 ${radius}px`
            : position === 'right'
            ? `0 ${radius}px ${radius}px 0`
            : position === 'top'
            ? `${radius}px ${radius}px 0 0`
            : `0 0 ${radius}px ${radius}px`,
        // Box shadow adapts to theme via CSS variables - using a single shadow that works for both
        boxShadow:
          position === 'left' ? '-2px 2px 8px rgba(0,0,0,0.2)' : position === 'top' ? '0 -2px 8px rgba(0,0,0,0.2)' : '2px 2px 8px rgba(0,0,0,0.2)',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          // keep it opaque on hover as well
          transform: 'scale(1.05)',
        },
      })}
    >
      {/* Two parallel lines - like an equals sign */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isVertical ? 'row' : 'column',
          gap: `${lineGap}px`,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={(theme) => ({
            width: isVertical ? lineThickness : lineLength,
            height: isVertical ? lineLength : lineThickness,
            bgcolor: theme.vars?.palette?.primary?.main,
            borderRadius: 1,
            opacity: 0.7,
          })}
        />
        <Box
          sx={(theme) => ({
            width: isVertical ? lineThickness : lineLength,
            height: isVertical ? lineLength : lineThickness,
            bgcolor: theme.vars?.palette?.primary?.main,
            borderRadius: 1,
            opacity: 0.7,
          })}
        />
      </Box>
    </Box>
  );
};

export default PullTab;
